// =============================================================
// 📁 src/pages/LoginPage.tsx — EspoirAfriqueSas (entrée SaaS métier)
// =============================================================

import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { AxiosError } from "axios";
import { api } from "../lib/axios";
import { getDefaultRouteForUser } from "../utils/roleAccess";

type EntryMode = "admin" | "client";

/** v2 : ignore l’ancienne valeur mémorisée (souvent un autre compte sur localhost). */
const REMEMBER_KEY = "espoir_login_remember_v2";

// =============================================================
// 🧩 Composant principal
// =============================================================
const LoginPage: React.FC = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation();

  const tr = (key: string, fallback: string) =>
    t(key, { defaultValue: fallback });

  const [entryMode, setEntryMode] = useState<EntryMode>(() => {
    if (location.pathname === "/client/login") return "client";
    const q = searchParams.get("espace");
    return q === "client" ? "client" : "admin";
  });

  const [identifiant, setIdentifiant] = useState("");
  const [motDePasse, setMotDePasse] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const [resetMode, setResetMode] = useState<"none" | "email">("none");
  const [resetEmail, setResetEmail] = useState("");

  useEffect(() => {
    try {
      let saved = localStorage.getItem(REMEMBER_KEY);
      if (!saved) {
        const legacy = localStorage.getItem("espoir_login_remember");
        const t = legacy?.trim() ?? "";
        if (
          legacy &&
          (t === "admin" || /@espoirafriquesas\.local$/i.test(t))
        ) {
          saved = t;
          localStorage.removeItem("espoir_login_remember");
          localStorage.setItem(REMEMBER_KEY, t);
        }
      }
      if (saved) {
        setIdentifiant(saved);
        setRememberMe(true);
      }
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    if (location.pathname === "/client/login" && entryMode !== "client") {
      setEntryMode("client");
    }
  }, [location.pathname, entryMode]);

  useEffect(() => {
    const next = entryMode === "client" ? "client" : "admin";
    const current = searchParams.get("espace");
    if (current !== next) {
      setSearchParams({ espace: next }, { replace: true });
    }
  }, [entryMode, setSearchParams, searchParams]);

  useEffect(() => {
    if (erreur) {
      const timer = setTimeout(() => setErreur(null), 6000);
      return () => clearTimeout(timer);
    }
  }, [erreur]);

  const setModeAdmin = () => setEntryMode("admin");
  const setModeClient = () => setEntryMode("client");

  // =============================================================
  // 🚀 Connexion utilisateur (payload inchangé pour le backend)
  // =============================================================
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isSubmitting) return;

    setErreur(null);
    setIsSubmitting(true);

    const idTrim = identifiant.trim();
    /** Aligné sur `normalizePasswordInput` backend (NFKC + trim + zero-width) */
    const passNormalized = motDePasse
      .normalize("NFKC")
      .replace(/[\u200B-\u200D\uFEFF]/g, "")
      .trim();

    if (!idTrim || !passNormalized) {
      setErreur(tr("login.fillAll", "Veuillez remplir tous les champs."));
      setIsSubmitting(false);
      return;
    }

    const identifiantEnvoi = idTrim.includes("@")
      ? idTrim.toLowerCase()
      : idTrim;

    try {
      const loginEndpoint = entryMode === "client" ? "/client-auth/login" : "/auth/login";

      const res = await api.post(
        loginEndpoint,
        { identifiant: identifiantEnvoi, motDePasse: passNormalized },
        { withCredentials: true }
      );

      const data = res.data;

      if (!data?.accessToken || !data?.user) {
        throw new Error("Réponse serveur invalide.");
      }

      login(data.user, data.accessToken);

      if (rememberMe) {
        localStorage.setItem(REMEMBER_KEY, identifiantEnvoi);
      } else {
        localStorage.removeItem(REMEMBER_KEY);
      }

      toast.success(`${tr("login.welcome", "Bienvenue")} ${data.user.nom}`);

      const role = String(data.user.role).toLowerCase();

      if (role === "client") {
        localStorage.setItem("clientPortalSession", "true");
      } else {
        localStorage.removeItem("clientPortalSession");
      }

      navigate(getDefaultRouteForUser(data.user), { replace: true });
    } catch (err: unknown) {
      const ae = err instanceof AxiosError ? err : null;
      const status = ae?.response?.status;
      const data = ae?.response?.data as
        | { error?: string; message?: string; status?: string }
        | undefined;
      let msg =
        data?.message ||
        data?.error ||
        tr(
          "login.errorGeneric",
          "Identifiants incorrects ou erreur serveur."
        );
      if (status === 403 && data?.error === "USER_LOGIN_BLOCKED_STATUS") {
        msg =
          data?.message ||
          "Ce compte est suspendu ou désactivé. Contactez l’administrateur.";
      }

      if (import.meta.env.DEV && status === 401) {
        msg += " — En local, compte test : admin@espoirafriquesas.local / admin123. Vérifiez que le navigateur n’a pas prérempli un autre e-mail (ex. demo.ecole) sur ce même localhost.";
      }

      setErreur(msg);
      toast.error(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  // =============================================================
  // 🔐 Reset par e-mail
  // =============================================================
  const handleResetEmail = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!resetEmail.trim()) {
      setErreur(tr("login.emailRequired", "Veuillez entrer votre adresse e-mail."));
      return;
    }

    try {
      const res = await api.post("/auth/forgot-password", {
        email: resetEmail,
      });

      toast.success(
        res.data?.message ||
          tr("login.resetSent", "Lien de réinitialisation envoyé.")
      );

      setResetMode("none");
    } catch (err: unknown) {
      const axiosErr = err as {
        response?: { data?: { error?: string; message?: string } };
      };
      const msg =
        axiosErr?.response?.data?.error ||
        axiosErr?.response?.data?.message ||
        tr(
          "login.resetError",
          "Erreur lors de la demande de réinitialisation."
        );

      setErreur(msg);
      toast.error(msg);
    }
  };

  const identifiantInvalid = Boolean(erreur);

  // =============================================================
  // 🧭 Rendu
  // =============================================================
  return (
    <div className="flex min-h-screen w-full flex-col bg-slate-950 text-slate-100 md:flex-row">
      {/* Colonne branding — moins dominante, meilleure lisibilité */}
      <div className="relative flex min-h-[220px] flex-col justify-between border-b border-slate-800 md:min-h-screen md:w-[42%] md:border-b-0 md:border-r">
        <div
          className="absolute inset-0 bg-gradient-to-br from-emerald-950 via-slate-900 to-slate-950 bg-cover bg-center"
          aria-hidden
        />
        <div className="absolute inset-0 bg-gradient-to-b from-slate-950/95 via-slate-950/88 to-slate-950/95 md:bg-gradient-to-r" />
        <div className="relative z-10 flex flex-1 flex-col justify-center px-6 py-8 md:px-10 md:py-12">
          <img
            src="/vite.svg"
            alt="EspoirAfriqueSas"
            className="mb-4 h-14 w-auto opacity-95 md:mb-6 md:h-20"
          />
          <h1 className="text-2xl font-bold leading-tight tracking-tight text-white md:text-3xl lg:text-4xl">
            Pilotez vos services terrain
          </h1>
          <p className="mt-3 max-w-md text-sm leading-relaxed text-slate-300 md:text-base">
            Abonnements, planification des passages et suivi des prestations —
            un seul espace pour vos équipes et vos clients.
          </p>

          <ul className="mt-6 space-y-2 text-sm text-slate-300">
            <li className="flex items-start gap-2">
              <span className="mt-0.5 font-semibold text-emerald-400">•</span>
              Connexion sécurisée et session protégée
            </li>
            <li className="flex items-start gap-2">
              <span className="mt-0.5 font-semibold text-emerald-400">•</span>
              Suivi des opérations et statuts en temps réel
            </li>
            <li className="flex items-start gap-2">
              <span className="mt-0.5 font-semibold text-emerald-400">•</span>
              Assistance et support dédiés EspoirAfriqueSas
            </li>
          </ul>
        </div>
        <footer className="relative z-10 border-t border-slate-800/80 px-6 py-3 text-center text-xs text-slate-500 md:border-0 md:px-10 md:pb-6 md:text-left">
          © {new Date().getFullYear()} EspoirAfriqueSas — Tous droits réservés.
        </footer>
      </div>

      {/* Colonne formulaire — équilibre visuel */}
      <div className="flex flex-1 flex-col items-center justify-center px-4 py-8 md:px-8">
        <div className="mt-10 w-full max-w-md md:mt-0">
          <div className="mb-6 flex flex-col gap-2 sm:flex-row sm:gap-3">
            <button
              type="button"
              onClick={setModeClient}
              className={`flex-1 rounded-lg border px-4 py-2.5 text-sm font-semibold transition ${
                entryMode === "client"
                  ? "border-emerald-500 bg-emerald-500/15 text-emerald-100"
                  : "border-slate-600 bg-slate-900 text-slate-300 hover:border-slate-500"
              }`}
            >
              Espace Client
            </button>
            <button
              type="button"
              onClick={setModeAdmin}
              className={`flex-1 rounded-lg border px-4 py-2.5 text-sm font-semibold transition ${
                entryMode === "admin"
                  ? "border-sky-500 bg-sky-500/15 text-sky-100"
                  : "border-slate-600 bg-slate-900 text-slate-300 hover:border-slate-500"
              }`}
            >
              Espace Administrateur
            </button>
          </div>

          <div className="rounded-2xl border border-slate-700/80 bg-slate-900/90 p-6 shadow-xl backdrop-blur-sm md:p-8">
            {resetMode === "none" ? (
              <>
                <p className="mb-1 text-center text-xs font-medium uppercase tracking-wide text-slate-500">
                  {entryMode === "client"
                    ? "Portail client"
                    : "Accès professionnel"}
                </p>
                <h2 className="mb-6 text-center text-2xl font-bold text-white">
                  {tr("login.title", "Connexion")}
                </h2>

                <form
                  onSubmit={handleSubmit}
                  className="space-y-4"
                  autoComplete="off"
                >
                  <div>
                    <label
                      htmlFor="login-identifiant"
                      className="mb-1.5 block text-sm font-medium text-slate-300"
                    >
                      Adresse e-mail
                    </label>
                    <input
                      id="login-identifiant"
                      name="espoirafriquesas-identifiant"
                      type="text"
                      inputMode="email"
                      autoComplete="section-espoirafriquesas username"
                      value={identifiant}
                      onChange={(e) => setIdentifiant(e.target.value)}
                      aria-invalid={identifiantInvalid}
                      className={`w-full rounded-lg border bg-slate-800/80 p-3 text-slate-100 outline-none transition placeholder:text-slate-500 focus:ring-2 ${
                        identifiantInvalid
                          ? "border-red-500 focus:ring-red-500/40"
                          : "border-slate-600 focus:border-sky-500 focus:ring-sky-500/30"
                      }`}
                      placeholder={
                        entryMode === "client"
                          ? "vous@exemple.com"
                          : "E-mail ou identifiant professionnel"
                      }
                      required
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="login-password"
                      className="mb-1.5 block text-sm font-medium text-slate-300"
                    >
                      Mot de passe
                    </label>
                    <div className="relative">
                      <input
                        id="login-password"
                        name="espoirafriquesas-password"
                        type={showPassword ? "text" : "password"}
                        autoComplete="section-espoirafriquesas current-password"
                        value={motDePasse}
                        onChange={(e) => setMotDePasse(e.target.value)}
                        aria-invalid={identifiantInvalid}
                        className={`w-full rounded-lg border bg-slate-800/80 p-3 pr-24 text-slate-100 outline-none transition focus:ring-2 ${
                          identifiantInvalid
                            ? "border-red-500 focus:ring-red-500/40"
                            : "border-slate-600 focus:border-sky-500 focus:ring-sky-500/30"
                        }`}
                        placeholder="••••••••"
                        required
                      />
                      <button
                        type="button"
                        className="absolute right-2 top-1/2 -translate-y-1/2 rounded px-2 py-1 text-xs font-medium text-slate-400 hover:bg-slate-700 hover:text-slate-200"
                        onClick={() => setShowPassword((v) => !v)}
                      >
                        {showPassword ? "Masquer" : "Afficher"}
                      </button>
                    </div>
                  </div>

                  <div className="flex items-center justify-between gap-3 pt-1">
                    <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-300">
                      <input
                        type="checkbox"
                        checked={rememberMe}
                        onChange={(e) => setRememberMe(e.target.checked)}
                        className="h-4 w-4 rounded border-slate-500 bg-slate-800 text-sky-600 focus:ring-sky-500"
                      />
                      Se souvenir de moi
                    </label>
                  </div>

                  {erreur && (
                    <div
                      role="alert"
                      className="rounded-lg border border-red-500/40 bg-red-950/40 px-3 py-2 text-center text-sm text-red-200"
                    >
                      {erreur}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="flex w-full items-center justify-center rounded-lg bg-sky-600 py-3 text-sm font-semibold text-white transition hover:bg-sky-500 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {isSubmitting
                      ? tr("login.loading", "Connexion...")
                      : tr("login.submit", "Se connecter")}
                  </button>
                </form>

                <button
                  type="button"
                  onClick={() => setResetMode("email")}
                  className="mt-4 w-full text-center text-sm text-sky-400 hover:text-sky-300 hover:underline"
                >
                  {tr("login.forgot", "Mot de passe oublié ?")}
                </button>
              </>
            ) : (
              <>
                <h2 className="mb-4 text-center text-xl font-semibold text-white">
                  {tr("login.resetTitle", "Réinitialisation")}
                </h2>

                <form onSubmit={handleResetEmail} className="space-y-4">
                  <input
                    type="email"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    className="w-full rounded-lg border border-slate-600 bg-slate-800/80 p-3 text-slate-100 outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-500/30"
                    placeholder={tr("login.emailPh", "Votre e-mail")}
                    required
                  />

                  <button
                    type="submit"
                    className="w-full rounded-lg bg-emerald-600 py-3 text-sm font-semibold text-white transition hover:bg-emerald-500"
                  >
                    {tr("login.sendLink", "Envoyer le lien")}
                  </button>
                </form>

                <button
                  type="button"
                  onClick={() => setResetMode("none")}
                  className="mt-4 w-full text-center text-sm text-slate-400 hover:text-slate-300 hover:underline"
                >
                  {tr("common.back", "Retour")}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
